import { EDITOR_CONSTANTS } from "@perceptron/constants/editorConstants";
import { logError } from "@perceptron/utils/errorHandling";
import { Extension } from "@tiptap/core";
import { Plugin, PluginKey, EditorState, Selection } from "@tiptap/pm/state";
import { DecorationSet, EditorView } from "@tiptap/pm/view";

import { isMobileViewport } from "@demo/hooks/useIsMobile";

export interface CaretBubblePosition {
  empty: boolean;
  from: number;
  isValidTarget: boolean;
  isEmptyParagraph?: boolean; // Whether this is an empty paragraph
  lineCoords: {
    top: number;
    bottom: number;
    height: number;
  };
  lineEndX: number; // X position at the end of the line
  cursorX: number; // X position of the cursor
}

export interface CaretBubbleExtensionOptions {
  onCaretPositionChange?: (position: CaretBubblePosition) => void;
  setMousePosition?: (position: { x: number; y: number }) => void;
}

// Helper function to check if caret is at a valid insertion point
export function isCaretAtValidInsertionPoint(state: EditorState): boolean {
  const { selection, doc } = state;

  if (!selection.empty) {
    return false;
  }

  const $from = doc.resolve(selection.from);

  if ($from.parent.type.name !== EDITOR_CONSTANTS.NODE_TYPES.PARAGRAPH) {
    return false;
  }

  // For empty paragraphs, allow bubble anywhere
  const isEmpty = $from.parent.content.size === 0;

  if (isEmpty) {
    return true;
  }

  // For non-empty paragraphs, must be at end of line
  // Note: parentOffset is the offset within the parent node
  const atEndOfLine = $from.parentOffset === $from.parent.content.size;

  if (!atEndOfLine) {
    return false;
  }

  // Check if previous node is media - if so, don't show bubble
  const prev = $from.nodeBefore;
  const prevIsMedia = prev && (EDITOR_CONSTANTS.MEDIA_NODE_TYPES as readonly string[]).includes(prev.type.name);

  if (prevIsMedia) {
    return false;
  }

  return true;
}

// Helper to check if two selections are the same
function sameSelection(a: Selection | undefined, b: Selection): boolean {
  if (!a) return false;
  return a.from === b.from && a.to === b.to && a.empty === b.empty;
}

export const CaretBubbleExtension = Extension.create<CaretBubbleExtensionOptions>({
  name: "caretBubble",

  addOptions() {
    return {
      onCaretPositionChange: undefined,
      setMousePosition: undefined,
    };
  },

  addProseMirrorPlugins() {
    // Disable entirely on mobile (viewport-based; the caret bubble is a
    // pointer-driven affordance and Overlay.tsx hides it on mobile too).
    if (isMobileViewport()) {
      return [];
    }

    /* eslint-disable-next-line @typescript-eslint/no-this-alias */
    const extensionInstance = this;

    return [
      new Plugin({
        key: new PluginKey("caretBubble"),
        state: {
          init() {
            return DecorationSet.empty;
          },
          apply(_tr, _oldState) {
            return DecorationSet.empty;
          },
        },
        view() {
          let prevFocused = false;
          let mousePosition = { x: 0, y: 0 };
          let mouseRafId: number | null = null;

          // Track mouse position for visibility checking. Store writes are
          // coalesced to one per animation frame — every write re-runs the
          // store's subscribers, so pushing on every raw mousemove event is
          // wasted work.
          const handleMouseMove = (e: MouseEvent) => {
            mousePosition = { x: e.clientX, y: e.clientY };
            const { setMousePosition } = extensionInstance.options;
            if (!setMousePosition || mouseRafId != null) {
              return;
            }
            mouseRafId = requestAnimationFrame(() => {
              mouseRafId = null;
              setMousePosition(mousePosition);
            });
          };

          // Start tracking mouse when editor is created
          document.addEventListener("mousemove", handleMouseMove);

          // Helper to calculate and send position update
          const updatePosition = (view: EditorView) => {
            const { onCaretPositionChange } = extensionInstance.options;
            if (!onCaretPositionChange) return;

            const { state } = view;
            const { selection } = state;
            const { empty, from } = selection;

            // Check if caret is at a valid insertion point
            const isValidTarget = isCaretAtValidInsertionPoint(state);

            // Send position updates to the store
            if (!isValidTarget) {
              // Clear position immediately if not valid
              onCaretPositionChange({
                from,
                empty,
                isValidTarget: false,
                lineCoords: { top: 0, bottom: 0, height: 0 },
                lineEndX: 0,
                cursorX: 0,
              });
              return;
            }

            // Get the current paragraph and calculate line end position
            const $from = state.doc.resolve(from);
            const paragraph = $from.parent;
            const paragraphStart = $from.start();
            const isEmpty = paragraph.content.size === 0;

            // Calculate the actual end position of text content
            const lineEndPos = paragraphStart + paragraph.content.size;

            // Get coordinates at cursor position for line bounds (vertical)
            const cursorCoords = view.coordsAtPos(from);

            // Get coordinates at end of line for horizontal position
            // Always use the actual end position, even for empty paragraphs
            const endCoords = view.coordsAtPos(lineEndPos);

            // Use cursor coords for vertical alignment (top/bottom)
            const lineHeight = cursorCoords.bottom - cursorCoords.top;

            const positionData = {
              from,
              empty,
              isValidTarget, // Always report true validity, regardless of mouse position
              isEmptyParagraph: isEmpty,
              lineCoords: {
                top: cursorCoords.top,
                bottom: cursorCoords.bottom,
                height: lineHeight,
              },
              // Always use end position for consistent horizontal placement
              lineEndX: endCoords.right,
              cursorX: cursorCoords.right,
            };

            // Defer callback to avoid flushSync during render
            queueMicrotask(() => {
              onCaretPositionChange(positionData);
            });
          };

          return {
            update: (view: EditorView, prevState: EditorState | null) => {
              try {
                const { state } = view;
                const { selection } = state;
                const prevSelection = prevState?.selection;
                const isFocused = view.hasFocus();

                // Check if document content changed
                const contentChanged = prevState && prevState.doc !== state.doc;

                // Force update on content change even if selection appears the same
                // This ensures bubble visibility is recalculated after typing/backspacing
                const shouldUpdate =
                  isFocused !== prevFocused ||
                  !sameSelection(prevSelection, selection) ||
                  contentChanged ||
                  // Additional check: if we're focused and at a valid position, always update
                  // This helps with recalculation after content changes
                  (isFocused && isCaretAtValidInsertionPoint(state));

                if (shouldUpdate) {
                  updatePosition(view);
                }

                prevFocused = isFocused;
              } catch (error) {
                logError(error, "CaretBubble.update");
              }
            },
            destroy: () => {
              // Clean up mouse event listener and any pending frame
              document.removeEventListener("mousemove", handleMouseMove);
              if (mouseRafId != null) {
                cancelAnimationFrame(mouseRafId);
                mouseRafId = null;
              }
            },
          };
        },
      }),
    ];
  },
});
